import React, { useEffect, useState, useCallback, forwardRef } from "react";

import { getFileExtension, formatBytes, convertMegabytesToBytes } from "utils";
import { parseForwardProps } from "utils";

import { useSnackbar } from "contexts";
import {
  FileTextIcon,
  FileVideoIcon,
  ImageIcon,
  CloudUploadIcon,
  XIcon,
  GripVerticalIcon,
  ZoomInIcon,
} from "lucide-react";
// Note: react-beautiful-dnd shows defaultProps deprecation warning in React 18+
// This is a known issue with the library and doesn't affect functionality
// Consider migrating to @dnd-kit or @hello-pangea/dnd in the future
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DroppableProvided,
  type DroppableStateSnapshot,
  type DraggableLocation,
} from "@hello-pangea/dnd";
import { useDropzone, type FileWithPath } from "react-dropzone";
import { v4 as uuid } from "uuid";

import {
  alpha,
  Stack,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Button,
  IconButton,
  FormControl,
  styled,
  useTheme,
  Zoom,
} from "@mui/material";

import {
  Image,
  hideScrollbar,
  fieldFill,
  fieldBorderColor,
  fieldAccentColor,
  fieldHoverRing,
  fieldFocusRing,
} from "@repo/ui";

// Direct path, NOT the `components` barrel: this file is itself re-exported by that barrel, so
// going through it would close a cycle (components → input → FileUploadField → components) and
// resolve to undefined at module-init time — an empty render with no error pointing here.
import { ImageGalleryModal } from "components/feedback/modals/ImageGalleryModal";

/** Dropzone height when the caller doesn't say — the product-gallery size. */
const DEFAULT_DROPZONE_HEIGHT = 268;

/**
 * Below this height the prompt switches to its compact arrangement. Derived from `height`
 * rather than exposed as a separate `dense` flag: the two would always be set together, and
 * a caller that set one without the other would get a clipped prompt.
 */
const COMPACT_BELOW = 200;

/** Fallback per-file cap, MB. Matches the limit this component hardcoded before it took a prop. */
const DEFAULT_MAX_SIZE_MB = 2;

interface FileUploadFieldProps {
  items?: string[];
  label?: string;
  description: string;
  onFilesChange?: (files: File[]) => void;
  acceptFiles: any;
  showMaxFileSize?: boolean;
  error?: string;
  /**
   * Dropzone height. Defaults to 268px, which suits a product image gallery and is far too
   * tall for a single optional attachment sitting under a five-row textarea.
   *
   * Below 200px the prompt tightens automatically (smaller glyph, and the format hint and the
   * size cap share one line) because the four full-size rows do not fit — at 140px they
   * overflow a 116px content box and clip.
   */
  height?: number | string;
  /**
   * Max size per file, in MB. Drives the dropzone's own limit, the "(Max file size: N MB)"
   * hint AND the too-large error, so those three can never disagree.
   *
   * They did disagree: the hint was hardcoded "10MB" while the dropzone enforced 2MB and the
   * error said "Max image size 2MB", so a 3MB screenshot was refused by a field that had just
   * advertised 10MB. One prop, one number.
   */
  maxSizeMb?: number;
  /**
   * Cap on how many files may be held. Enforced on drop, and passed to the dropzone so extra
   * files are rejected rather than silently dropped on the floor — the support form asks for
   * `maxFiles={1}` and submits `files[0]`, so without this a second drop vanished with no word.
   */
  maxFiles?: number;
  /**
   * Heading for the full-size viewer opened from a thumbnail. Defaults to "Images"; the support
   * form sets "Attachment" because that is the word its own label uses.
   */
  galleryTitle?: string;
  /**
   * Glyph in the empty dropzone. Defaults to `CloudUploadIcon` — correct wherever the field is
   * "put a file here" and nothing more.
   *
   * A caller overrides it when the field is about ONE specific subject and the generic cloud says
   * less than the subject's own mark would: the scan-media field passes `TagLocationIcon`, an item
   * with a pin planted on it, which is the still frame of what that field holds.
   *
   * Takes the component, not an element, because this file owns the sizing — the prompt tightens
   * below `COMPACT_BELOW` and the glyph has to tighten with it. An element passed in would carry
   * its own `size` and ignore that.
   */
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}

type File = {
  id: string;
  file: FileWithPath;
  preview_url: string;
  isUploaded: boolean;
};

const reorder = (items: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(items);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const FileUploadField = forwardRef<
  HTMLInputElement,
  FileUploadFieldProps
>(
  (
    {
      items,
      label,
      description,
      onFilesChange,
      acceptFiles = {},
      showMaxFileSize = false,
      error,
      height = DEFAULT_DROPZONE_HEIGHT,
      maxSizeMb = DEFAULT_MAX_SIZE_MB,
      maxFiles,
      galleryTitle,
      icon: PromptIcon = CloudUploadIcon,
    },
    ref
  ) => {
    const isCompact =
      typeof height === "number" && height < COMPACT_BELOW;
    const [files, setFiles] = useState<File[]>([]);
    const [isItemsLoading, setIsItemsLoading] = useState(true);
    const theme = useTheme();
    /*
      ONE cast, for the field-chrome helpers below.

      The pnpm store holds two @mui/material copies (one resolved for react 18, one for
      react 19), so the `Theme` this file's `useTheme()` returns is nominally incompatible
      with the `Theme` in `@repo/ui`'s helper signatures — same object at runtime, different
      declaration file. It is a repo-wide condition, not a local one: 28 occurrences today,
      six of them in `DatePickerField` calling these very helpers.

      Cast once here rather than at each call site, so the workaround is visible and counted
      in one place instead of scattered. The real fix is deduplicating MUI (VRG-315).
    */
    const fieldTheme = theme as Parameters<typeof fieldFill>[0];

    const { open: notification } = useSnackbar();

    useEffect(() => {
      // Do NOT report an empty list while `items` are still being fetched into
      // blobs. `files` starts `[]`, so this effect fires once on mount, and a
      // consumer that mirrors it into form state (ProductEditPage does:
      // `onFilesChange` -> `field.onChange`) had its seeded value overwritten
      // with `[]` before the images finished loading. The gallery then showed
      // ten images while the form insisted "At least one image is required".
      //
      // Only the initial empty emit is suppressed. A user genuinely removing
      // the last image still reports `[]`, because by then loading is done.
      if (isItemsLoading && files.length === 0 && items?.length) return;
      onFilesChange?.(files);
    }, [files, isItemsLoading]); // onFilesChange omitted deliberately — it is a new identity each render

    const getBlobImages = async (items: string[]) => {
      const images = await Promise.all(
        items.map(async (image: string) => {
          const file = await fetch(image)
            .then((res) => res.blob())
            .then((blob) => {
              const filename = image.substring(image.lastIndexOf("/") + 1);
              return new File([blob], filename, { type: blob.type });
            });

          return {
            id: uuid(),
            file,
            preview_url: image,
            isUploaded: true,
          };
        })
      );
      return images;
    };

    const getImages = useCallback(async (items: string[]) => {
      setIsItemsLoading(true);
      const images = await getBlobImages(items);
      setFiles(images);
      setIsItemsLoading(false);
    }, []);

    const onDrop = useCallback(
      (acceptedFiles: FileWithPath[], rejectedFiles: any[]) => {
        const acceptedFilesWithPreview = acceptedFiles.map((file) => ({
          id: uuid(),
          file,
          preview_url: URL.createObjectURL(file),
          isUploaded: false,
        }));

        if (acceptedFilesWithPreview.length > 0) {
          setFiles((state) => {
            const next = [...state, ...acceptedFilesWithPreview];
            if (maxFiles === undefined) return next;
            // Keep the FIRST maxFiles, so an accidental extra drop cannot displace the file
            // the user already chose. Revoke the previews of anything turned away, or the
            // blob URLs leak (the unmount cleanup only walks what made it into state).
            const kept = next.slice(0, maxFiles);
            next
              .slice(maxFiles)
              .forEach((f) => URL.revokeObjectURL(f.preview_url));
            if (kept.length < next.length) {
              notification({
                type: "error",
                title:
                  maxFiles === 1
                    ? "You can attach one file."
                    : `You can attach up to ${maxFiles} files.`,
              });
            }
            return kept;
          });
        }

        if (rejectedFiles.length > 0) {
          const { errors } = rejectedFiles[0];

          errors.forEach((error) => {
            if (error.code === "file-too-large") {
              notification({
                type: "error",
                title: `File is too large. Max file size ${maxSizeMb}MB.`,
              });
            }

            if (error.code === "file-invalid-type") {
              notification({
                type: "error",
                title:
                  'You can upload "heic", "png", "jpg", "jpeg" file formats only',
              });
            }
          });
        }
      },
      [notification, maxFiles, maxSizeMb]
    );

    useEffect(() => {
      if (items && items.length > 0) {
        try {
          getImages(items);
        } catch (error) {
          console.error(error);
        }
      } else {
        setIsItemsLoading(false);
      }
    }, [items, getImages]);

    const {
      getRootProps,
      getInputProps,
      open,
      isFocused,
      isDragAccept,
      isDragReject,
    } = useDropzone({
      accept: acceptFiles,
      noClick: false,
      noKeyboard: false,
      maxSize: convertMegabytesToBytes(maxSizeMb),
      onDrop,
    });

    useEffect(() => {
      // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
      return () =>
        files.forEach((file) => URL.revokeObjectURL(file.preview_url));
    }, []);

    const handleDragEnd = ({
      destination,
      source,
    }: {
      destination: DraggableLocation;
      source: DraggableLocation;
    }) => {
      if (!destination) return;

      setFiles((files) => reorder(files, source.index, destination.index));
    };

    const handleDeleteItem = (id: string) => {
      setFiles((files) => {
        return files.filter((file) => file.id !== id);
      });
    };

    return (
      // TODO: Break this out into FIleUploadFieldField under custom Fields and keep the dropZone component here under common
      // `fullWidth` because MUI's FormControl is `inline-flex` and shrinks to its
      // CONTENT by default, which silently defeated the `width: '100%'` on the
      // dropzone below: in any container that doesn't stretch its children the
      // drop target collapsed to a narrow box while the fields above and below it
      // ran the full width. Caught in the font-upload modal (VRG-348). The call
      // sites that looked right were being stretched by their own parents, so
      // this was luck rather than layout.
      <FormControl fullWidth>
        {label && (
          <Typography mb={4} variant="body2">
            {label}
          </Typography>
        )}
        <Grid item md={12} sm={12}>
          <StyledDropzone
            {...getRootProps({ isFocused, isDragAccept, isDragReject })}
            sx={{
              borderRadius: 2,
              // Rest border from the SHARED helper, not a hand-rolled mode ternary. It already
              // resolved to the same value in dark mode (`primary[100]`) but to `grey[300]` in
              // light, one step darker than every other field — and a copy of a token is a copy
              // that drifts (§31: one source for label, border and fill). Dashed still marks it
              // as a drop target; only the colour is shared.
              border: `1px dashed ${fieldBorderColor(fieldTheme)}`,
              cursor: "pointer",
              height: typeof height === "number" ? `${height}px` : height,
              // Overrides StyledDropzone's 36px vertical padding. The prompt is centred by its
              // own full-height Stack, so that padding never positioned anything — it only ate
              // 72px of the box, which a compact dropzone cannot spare.
              padding: isCompact ? "12px" : "36px 0",
              width: "100%",
              // THE FILL, and the reason this component looked like a different KIND of control
              // than the textarea directly above it. `grey[50]` is a page-surface grey; fields
              // fill with `fieldFill` (#fafafa light / #1f1f23 dark). Sitting side by side in
              // one form, one filled like a field and one like a panel, they read as unrelated
              // widgets. `DatePickerField`, `DateRangePickerFilter` and `CreditCardField` all
              // already use this helper — the dropzone was the outlier.
              backgroundColor: fieldFill(fieldTheme),
              // Matches the 0.2s transition every other field carries, so the dropzone
              // and the Input above it reach their hover state at the same rate rather
              // than one snapping while the other eases.
              transition: "border-color 0.2s, box-shadow 0.2s",
              // HOVER + FOCUS rings, from the SAME `fieldChrome` source as every other
              // field (§31). The dropzone had the brand border on hover but NO ring, and
              // no `:focus-within` rule at all — so it was the one interactive field that
              // sat outside the rest → faint-ring → full-ring progression. §31's own text
              // already asserted this component had "the same brand border + soft ring on
              // hover"; only the border half had ever shipped.
              //
              // `focus-within`, not `focus`, because there are TWO focusable elements
              // here and either can hold focus: react-dropzone's `getRootProps()` puts
              // `tabIndex=0` on this container (so it is keyboard-reachable), AND the
              // nested `<input {...getInputProps()} />` is a real, non-hidden input that
              // takes focus when the file dialog is dismissed. `&:focus` would cover only
              // the first, leaving the ring off in the second case. `:focus-within`
              // matches when the element OR any descendant has focus, so it covers both.
              //
              // Note this rule also OVERRIDES `getColor()`'s `isFocused` branch, which
              // returns a hardcoded `#2196f3` (Material blue) — stock react-dropzone
              // example colour, never themed. Verified in the browser: the focused border
              // computes to rgb(127,72,223), not blue. The dead branch is left in place
              // rather than fixed here because `getColor` also owns the drag-accept /
              // drag-reject colours (`#00e676` / `#ff1744`), and re-theming those is a
              // separate decision from adding the ring.
              "&:hover": {
                border: `1px dashed ${fieldAccentColor(fieldTheme)}`,
                boxShadow: fieldHoverRing(fieldTheme),
              },
              "&:focus-within": {
                border: `1px dashed ${fieldAccentColor(fieldTheme)}`,
                boxShadow: fieldFocusRing(fieldTheme),
              },
            }}
          >
            <input {...getInputProps()} />
            {/* The whole prompt sits in the FADED register, because an empty dropzone is the
                same state as an empty textarea: a field with nothing in it, showing text that
                says what to put there. The textarea's placeholder is faded — measured, MUI
                renders it at effective alpha 0.365 (text.primary 0.87 x its own 0.42 opacity) —
                while this prompt was at the full 0.87, so side by side one empty field looked
                filled and the other looked empty.
                It does NOT go all the way to 0.365: the hint below is text.secondary (0.6), and
                matching the placeholder exactly would put the prompt BELOW its own subtitle in
                contrast, inverting the hierarchy. So both lines step down one level instead —
                secondary for the prompt, disabled for the hint — which keeps their order while
                moving the pair out of value-contrast. The icon takes the Stack's colour (lucide
                strokes with currentColor) so it fades with the text rather than staying the
                darkest thing in an empty box.
                NB: this comment lives HERE, as a child of the dropzone, and NOT inside the
                ternary below. A brace-wrapped JSX comment is only a comment in CHILD position;
                in a parenthesised expression position it parses as an empty object literal, and
                putting one there took the whole app blank via a failed HMR reload. (Nor can its
                prose contain a comment terminator, which closes it early — same blank page.) */}
            {isItemsLoading ? (
              <CircularProgress color="inherit" />
            ) : (
              <Stack
                spacing={isCompact ? "8px" : "12px"}
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%", color: "text.secondary" }}
              >
                <PromptIcon
                  size={isCompact ? 24 : 32}
                  strokeWidth={1}
                />

                {/* body3, matching the FieldLabel above the dropzone — NOT body2.
                    This text is field CHROME (an instruction), not an input VALUE. Value text
                    is 16px because it is what the user typed; a label is 14 and helper copy is
                    12. At body2 this prompt was the largest type in the whole field block —
                    larger than its own label — so the dropzone read as a heading with a
                    paragraph rather than as one field among several. */}
                <Typography variant="body3" color="text.secondary">
                  Click or drop files to upload
                </Typography>

                {/* Compact folds the format hint and the size cap onto ONE line — the same two
                    facts, in the vertical space of one, so a 140px dropzone still states both
                    rather than dropping the cap or clipping.
                    body4 to match the helper line that sits directly below the dropzone, so the
                    two secondary sentences either side of the border are the same size. */}
                <Typography variant="body4" color="text.disabled">
                  {description}
                  {isCompact && showMaxFileSize && ` · Max ${maxSizeMb}MB`}
                </Typography>
                {showMaxFileSize && !isCompact && (
                  <Typography variant="body4" color="text.disabled">
                    (Max file size: {maxSizeMb}MB)
                  </Typography>
                )}
              </Stack>
            )}
          </StyledDropzone>
        </Grid>
        <Grid item md={12} sm={12} sx={{ mt: files.length > 0 ? 4 : 0 }}>
          {files.length > 0 && (
            <FileList
              items={files}
              onDragEnd={handleDragEnd}
              onDeleteFile={handleDeleteItem}
              galleryTitle={galleryTitle}
            />
          )}
          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1, fontSize: "0.75rem" }}
            >
              {error}
            </Typography>
          )}
        </Grid>
      </FormControl>
    );
  }
);

/**
 * Whether a held file can be shown in an `<img>`.
 *
 * Reads the MIME type first and only falls back to the extension. The previous test asked
 * whether the FILENAME contained "jpg"/"png"/"gif"/…, which is true of `gifts.pdf` and
 * `png-export-notes.txt` — both would have been handed to an `<img>` and rendered as a broken
 * image. Substring-matching a name is not a type check.
 */
const isImageFile = (file: { name: string; type?: string }): boolean => {
  if (file.type) return file.type.startsWith("image/");
  return /\.(jpe?g|png|gif|webp|heic|avif|bmp|svg)$/i.test(file.name);
};

/**
 * Whether a held file is a video.
 *
 * Separate from the image test because a video is neither: it cannot go in an `<img>` (so it is
 * not in the gallery) but it is also not a document, and giving it the document glyph made a
 * screen recording look like a PDF in the list. Same MIME-first, extension-fallback shape.
 */
const isVideoFile = (file: { name: string; type?: string }): boolean => {
  if (file.type) return file.type.startsWith("video/");
  return /\.(mp4|m4v|mov|webm|mkv|avi)$/i.test(file.name);
};

const FileList = ({ items = [], onDragEnd, onDeleteFile, galleryTitle }: any) => {
  const deleteFile = (id: string) => {
    onDeleteFile(id);
  };

  const theme = useTheme();
  const status = "Complete";

  // Only the renderable files go to the lightbox, and `galleryIndexById` maps a clicked ROW
  // back to its position in that filtered array. Without the map, clicking the second image in
  // a list of [pdf, image, image] would open the third — the row index and the gallery index
  // are different coordinate systems the moment a non-image is present.
  const imageItems = items.filter((item: any) => isImageFile(item.file));
  const galleryImages = imageItems.map((item: any) => item.preview_url);
  const galleryIndexById = new Map<string, number>(
    imageItems.map((item: any, index: number) => [item.id, index])
  );

  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
          <Stack
            justifyContent="flex-start"
            ref={provided.innerRef}
            {...provided.droppableProps}
            // style={{
            //   background: snapshot.isDraggingOver ? theme.palette.grey[50] : 'transparent',
            // }}
            sx={{
              maxHeight: "320px",
              overflowY: "auto",
              overflowX: "hidden",
              flexShrink: 0,
              // Single bordered container; rows inside are separated by dividers
              // (no per-item boxes — that read as stacked repeated cards).
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: 2,
              ...hideScrollbar,
            }}
          >
            {items.map((item: any, index: number) => (
              <Draggable key={item.id} index={index} draggableId={item.id}>
                {(
                  provided: DraggableProvided,
                  snapshot: DraggableStateSnapshot
                ) => {
                  return (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        background: snapshot.isDragging
                          ? theme.palette.white[100]
                          : "transparent",
                      }}
                    >
                      {/* <Zoom in={true}> */}
                      <Grid
                        container
                        {...provided.dragHandleProps}
                        direction="row"
                        alignItems="center"
                        sx={{
                          px: 4,
                          py: 3,
                          backgroundColor: "bgColor.main",
                          borderBottom:
                            index < items.length - 1
                              ? `1px solid ${theme.palette.grey[200]}`
                              : "none",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          sx={{ width: "100%" }}
                        >
                          <Stack
                            direction="row"
                            spacing={4}
                            alignItems="center"
                            sx={{ maxWidth: 360 }}
                          >
                            <Stack>
                              <GripVerticalIcon strokeWidth={1} />
                            </Stack>
                            {(() => {
                              const isImage = isImageFile(item.file);
                              const canOpen =
                                isImage && Boolean(item.preview_url);
                              return (
                                <Stack
                                  alignItems="center"
                                  justifyContent="center"
                                  flexShrink={0}
                                  onClick={
                                    canOpen
                                      ? (e) => {
                                          // The row is a drag handle; without this the click
                                          // is also read as the start of a drag gesture.
                                          e.stopPropagation();
                                          setGalleryIndex(
                                            galleryIndexById.get(item.id) ?? 0
                                          );
                                        }
                                      : undefined
                                  }
                                  role={canOpen ? "button" : undefined}
                                  tabIndex={canOpen ? 0 : undefined}
                                  aria-label={
                                    canOpen
                                      ? `View ${item.file.name} full size`
                                      : undefined
                                  }
                                  onKeyDown={
                                    canOpen
                                      ? (e) => {
                                          if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                          ) {
                                            e.preventDefault();
                                            setGalleryIndex(
                                              galleryIndexById.get(item.id) ?? 0
                                            );
                                          }
                                        }
                                      : undefined
                                  }
                                  sx={(theme) => ({
                                    height: 64,
                                    width: 64,
                                    overflow: "hidden",
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.grey[50],
                                    position: "relative",
                                    ...(canOpen && {
                                      cursor: "zoom-in",
                                      // Hover/focus affordance: the thumbnail is small enough
                                      // that a cursor change alone is easy to miss, so the
                                      // magnifier appears over a scrim to say it opens.
                                      "& .thumb-overlay": {
                                        opacity: 0,
                                        transition:
                                          theme.transitions.create("opacity"),
                                      },
                                      "&:hover .thumb-overlay, &:focus-visible .thumb-overlay":
                                        { opacity: 1 },
                                      "&:focus-visible": {
                                        outline: `2px solid ${theme.palette.brand[600]}`,
                                        outlineOffset: 1,
                                      },
                                    }),
                                  })}
                                >
                                  {isImage ? (
                                    item.preview_url ? (
                                      <>
                                        <Image
                                          sx={{
                                            height: 64,
                                            width: 64,
                                            minWidth: 64,
                                          }}
                                          src={item.preview_url}
                                          fit="cover"
                                        />
                                        <Stack
                                          className="thumb-overlay"
                                          alignItems="center"
                                          justifyContent="center"
                                          sx={{
                                            position: "absolute",
                                            inset: 0,
                                            color: "common.white",
                                            backgroundColor: alpha(
                                              "#000000",
                                              0.45
                                            ),
                                          }}
                                        >
                                          <ZoomInIcon
                                            size={20}
                                            strokeWidth={1.25}
                                          />
                                        </Stack>
                                      </>
                                    ) : (
                                      <ImageIcon strokeWidth={1} />
                                    )
                                  ) : (
                                    <>
                                      {isVideoFile(item.file) ? (
                                        <FileVideoIcon strokeWidth={1} />
                                      ) : (
                                        <FileTextIcon strokeWidth={1} />
                                      )}
                                      <Typography noWrap variant="body3">
                                        {getFileExtension(item.file.name)}
                                      </Typography>
                                    </>
                                  )}
                                </Stack>
                              );
                            })()}

                            <Stack spacing={2}>
                              <Typography
                                sx={{ maxWidth: "250px" }}
                                variant="body2"
                                noWrap
                              >
                                {item.file.name}
                              </Typography>
                              <Typography
                                variant="body3"
                                color="text.secondary"
                              >
                                ({formatBytes(item.file.size)})<span> • </span>
                                {status}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack>
                            <IconButton
                              onClick={() => deleteFile(item.id)}
                              sx={{
                                marginLeft: 4,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.error.main,
                                    0.1
                                  ),
                                  color: theme.palette.error.main,
                                },
                              }}
                            >
                              <XIcon strokeWidth={1} />
                            </IconButton>
                            {/* {isItemLoading && <CircularProgress color="inherit" size={24} />} */}
                          </Stack>
                        </Stack>
                      </Grid>
                      {/* </Zoom> */}
                    </div>
                  );
                }}
              </Draggable>
            ))}
          </Stack>
        )}
      </Droppable>

      {/* Reuses the shared lightbox rather than a local one, so the attachment gallery and any
          other full-size viewer stay one component (§36). Order matches the list above. */}
      <ImageGalleryModal
        open={galleryIndex !== null}
        onClose={() => setGalleryIndex(null)}
        images={galleryImages}
        initialIndex={galleryIndex ?? 0}
        title={galleryTitle}
      />
    </DragDropContext>
  );
};

const getColor = (props: any) => {
  if (props.isDragAccept) {
    return "#00e676";
  }
  if (props.isDragReject) {
    return "#ff1744";
  }
  if (props.isFocused) {
    return "#2196f3";
  }
  return "#eeeeee";
};

const StyledDropzone = styled(Box, {
  shouldForwardProp: parseForwardProps([
    "isDragAccept",
    "isDragReject",
    "isFocused",
  ]),
})(({ theme, ...rest }) => ({
  width: "100%",
  height: "100%",
  border: `1px dashed ${theme.palette.grey[200]}`,
  borderColor: getColor(rest),
  padding: "36px 0",
  textAlign: "center",
}));
